<template>
    <div
        id="manual-step-4"
        class="manual-step flex flex-col h-full"
    >
        <!-- Main Container -->
        <div
            class="flex p-6 flex-col items-center gap-8 rounded-none md:rounded-lg bg-white dark:bg-slate-800 pb-24 md:pb-6 flex-1 min-h-0 overflow-y-auto"
        >
            <!-- Header Container -->
            <div class="flex items-start justify-between self-stretch">
                <div class="flex flex-col items-start gap-1 flex-1">
                    <!-- Title -->
                    <h1
                        class="self-stretch text-slate-800 dark:text-slate-100 text-xl font-medium leading-7"
                    >
                        Add card
                    </h1>

                    <!-- Card name and last four -->
                    <p
                        class="text-slate-500 dark:text-slate-400 flex items-center gap-2 text-sm font-normal leading-5"
                        v-if="manualChosenCard"
                    >
                        <img
                            v-if="manualChosenCard.card_image"
                            :src="`${cdnUrl}/${manualChosenCard.card_image.path}/_small/${manualChosenCard.card_image.file_name}`"
                            :alt="selectedCardAndUserHeader"
                            class="w-8"
                        />
                        <span class="inline-block">
                            {{ selectedCardAndUserHeader }}
                        </span>
                    </p>
                </div>
                <button
                    type="button"
                    @click="$emit('close')"
                    class="btn-icon btn-transparent w-10"
                >
                    <Icon icon="close" />
                </button>
            </div>

            <!-- Mobile step lines (Step 4) -->
            <div
                class="flex md:hidden px-6 pb-4 justify-center items-center gap-2 self-stretch"
            >
                <div class="flex-1 h-1 rounded bg-slate-200"></div>
                <div class="flex-1 h-1 rounded bg-slate-200"></div>
                <div class="flex-1 h-1 rounded bg-slate-800"></div>
            </div>

            <!-- Content Section -->
            <div class="flex flex-col items-start gap-6 self-stretch">
                <h2
                    class="text-slate-800 dark:text-slate-100 text-base font-medium leading-6"
                >
                    Welcome bonus
                </h2>
                <WelcomeBonusForm
                    v-model:welcome-bonus="localWelcomeBonus"
                    v-model:tiered-bonuses="localTieredBonuses"
                    :errors="fieldErrors"
                    @delete="$emit('resetBonuses')"
                />
            </div>
        </div>

        <!-- Mobile bottom bar: Back/Finish -->
        <div
            class="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] z-[1000]"
        >
            <div class="grid grid-cols-2 gap-2">
                <button
                    type="button"
                    @click="$emit('gotoManualStep', 3)"
                    class="w-full py-3 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                    Back
                </button>
                <button
                    type="button"
                    @click="$emit('addCard')"
                    class="w-full py-3 rounded-md bg-slate-800 text-white text-sm font-medium leading-5 hover:bg-slate-700"
                >
                    Finish
                </button>
            </div>
        </div>

        <!-- Footer -->
        <div
            class="hidden md:flex justify-between items-center p-6 bg-white dark:bg-slate-800 flex-shrink-0 mt-auto w-full"
        >
            <!-- Back Button -->
            <button
                type="button"
                @click="$emit('gotoManualStep', 3)"
                class="flex py-2.5 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
                Back
            </button>

            <!-- Progress indicator -->
            <div class="flex gap-2">
                <div class="w-6 h-1 rounded-sm bg-slate-200"></div>
                <div class="w-6 h-1 rounded-sm bg-slate-200"></div>
                <div class="w-6 h-1 rounded-sm bg-slate-800"></div>
            </div>

            <!-- Right buttons -->
            <div class="flex gap-3">
                <!-- Cancel Button -->
                <button
                    type="button"
                    @click="$emit('close')"
                    class="flex py-2.5 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                    Cancel
                </button>

                <!-- Finish Button -->
                <button
                    type="button"
                    @click="$emit('addCard')"
                    class="flex py-2.5 px-4 justify-center items-center gap-2 rounded bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-white text-sm font-medium leading-5 hover:bg-slate-700 transition-colors"
                >
                    Finish
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import WelcomeBonusForm from '@/components/wallet/partials/welcome-bonus-form.vue';

const props = defineProps({
    manualChosenCard: { type: Object, default: null },
    welcomeBonus: { type: Object, required: true },
    tieredBonuses: { type: Array, required: true },
    errors: { type: Array, required: true },
    fieldErrors: { type: Object, default: () => ({}) },
    selectedCardAndUserHeader: { type: String, default: '' },
});

const emit = defineEmits([
    'close',
    'gotoManualStep',
    'addCard',
    'resetBonuses',
    'update:welcomeBonus',
    'update:tieredBonuses',
]);

const cdnUrl = computed(() => window.cdnUrl);

const localWelcomeBonus = computed({
    get: () => props.welcomeBonus,
    set: (val) => emit('update:welcomeBonus', val),
});

const localTieredBonuses = computed({
    get: () => props.tieredBonuses,
    set: (val) => emit('update:tieredBonuses', val),
});
</script>
